<script setup>
// Painel de filtros da tela de Vincular Prognose, no padrão do
// FireAlertsFilter (dashboard de risco de incêndio): botão-ícone que abre um
// painel flutuante com os filtros em RASCUNHO — nada é aplicado até o
// "filtrar" — rodapé limpar/filtrar e fechamento por clique fora.
import { computed, reactive, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import Papa from 'papaparse'
import SFButton from '@/components/ui/button/Button.vue'
import Dropdown from '@/components/ui/dropdown/Dropdown.vue'

const props = defineProps({
	/** Listas brutas dos dropdowns do Registry (id/codigo/nome|descricao). */
	fazendas: { type: Array, default: () => [] },
	talhoes: { type: Array, default: () => [] },
	estratos: { type: Array, default: () => [] },
	/** Triplas {fazendaId, estratoId, talhaoId} do acervo — base da cascata. */
	combinacoes: { type: Array, default: () => [] },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['filtrar', 'limpar'])

const painel = ref()
const inputCsv = ref()
const aberto = ref(false)

// Rascunho: só vira filtro de verdade quando o usuário clica em "filtrar".
const rascunho = reactive({
	fazendasIds: [],
	talhoesIds: [],
	estratosIds: [],
})

// Resumo do último CSV importado (contagens + itens sem correspondência).
const csvResumo = ref(null)

// Feedback do "copiar não encontrados" (volta ao normal após 2s).
const copiado = ref(false)

async function copiarNaoEncontrados() {
	const itens = csvResumo.value?.naoEncontrados ?? []
	if (itens.length === 0) return
	await navigator.clipboard.writeText(itens.join('\n'))
	copiado.value = true
	setTimeout(() => {
		copiado.value = false
	}, 2000)
}

const temFiltroNoRascunho = computed(
	() =>
		rascunho.fazendasIds.length > 0 ||
		rascunho.talhoesIds.length > 0 ||
		rascunho.estratosIds.length > 0,
)

const fazendasOptions = computed(() => props.fazendas.map((f) => ({ value: f.id, label: f.nome })))

// --- Cascata (Fazenda -> Estrato -> Talhão) sobre as triplas do acervo ---
// null = sem restrição (nenhum nível acima selecionado).
const estratosPermitidos = computed(() => {
	if (rascunho.fazendasIds.length === 0) return null
	const fazendas = new Set(rascunho.fazendasIds)
	return new Set(props.combinacoes.filter((c) => fazendas.has(c.fazendaId)).map((c) => c.estratoId))
})

const talhoesPermitidos = computed(() => {
	if (rascunho.fazendasIds.length === 0 && rascunho.estratosIds.length === 0) return null
	const fazendas = new Set(rascunho.fazendasIds)
	const estratos = new Set(rascunho.estratosIds)
	return new Set(
		props.combinacoes
			.filter(
				(c) =>
					(fazendas.size === 0 || fazendas.has(c.fazendaId)) &&
					(estratos.size === 0 || estratos.has(c.estratoId)),
			)
			.map((c) => c.talhaoId)
			.filter(Boolean),
	)
})

const talhoesOptions = computed(() =>
	props.talhoes
		.filter((t) => !talhoesPermitidos.value || talhoesPermitidos.value.has(t.id))
		.map((t) => ({
			value: t.id,
			label: t.fazenda ? `${t.codigo} — ${t.fazenda}` : `${t.codigo}`,
		})),
)

// Código no label: o ui/Dropdown remove chips localizando a opção pelo LABEL —
// descrições de estrato repetem entre fazendas e removeriam o item errado.
const estratosOptions = computed(() =>
	props.estratos
		.filter((e) => !estratosPermitidos.value || estratosPermitidos.value.has(e.id))
		.map((e) => ({
			value: e.id,
			label: e.codigo != null ? `${e.codigo} — ${e.descricao}` : e.descricao,
		})),
)

// Poda: seleção que ficou fora da cascata (trocou a fazenda/estrato acima)
// sai do rascunho — senão viraria filtro invisível na busca.
// Reatribui SÓ quando algo foi podado: atribuição incondicional criaria novo
// array a cada disparo e re-acionaria o próprio watch em loop.
function podar(lista, permitidos) {
	if (!permitidos) return lista
	const filtrados = lista.filter((id) => permitidos.has(id))
	return filtrados.length === lista.length ? lista : filtrados
}

watch(
	() => [...rascunho.fazendasIds],
	() => {
		rascunho.estratosIds = podar(rascunho.estratosIds, estratosPermitidos.value)
		rascunho.talhoesIds = podar(rascunho.talhoesIds, talhoesPermitidos.value)
	},
)

watch(
	() => [...rascunho.estratosIds],
	() => {
		rascunho.talhoesIds = podar(rascunho.talhoesIds, talhoesPermitidos.value)
	},
)

function abrir() {
	aberto.value = true
}

function fechar() {
	aberto.value = false
}

function aplicar() {
	emit('filtrar', {
		fazendasIds: [...rascunho.fazendasIds],
		talhoesIds: [...rascunho.talhoesIds],
		estratosIds: [...rascunho.estratosIds],
		csvResumo: csvResumo.value,
	})
	fechar()
}

function limpar() {
	rascunho.fazendasIds = []
	rascunho.talhoesIds = []
	rascunho.estratosIds = []
	csvResumo.value = null
	emit('limpar')
	fechar()
}

/**
 * Lê o arquivo detectando o encoding: UTF-8 (com/sem BOM) primeiro — decode
 * estrito falha se não for UTF-8 válido — e cai para ISO-8859-1 (Excel pt-BR).
 * O encoding fixo anterior corrompia acentos de arquivos UTF-8.
 */
async function lerArquivoComoTexto(file) {
	const buffer = await file.arrayBuffer()
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
	} catch {
		return new TextDecoder('iso-8859-1').decode(buffer)
	}
}

/**
 * CSV com colunas "Fazenda" e/ou "Talhao" (com/sem acento; , ou ;), tratado
 * como PARES por linha — códigos de talhão se repetem entre fazendas, e
 * resolver as colunas como listas independentes fazia o filtro (AND) devolver
 * o produto cartesiano: com um arquivo real de 12,8k pares, ~7k plantios de
 * outras combinações vazavam para o resultado.
 *
 * Semântica por linha:
 *  - fazenda + talhão: só o talhão DAQUELA fazenda entra no filtro;
 *  - só fazenda: a fazenda inteira (todos os talhões dela entram, para o AND
 *    do back continuar exato junto dos pares);
 *  - só talhão (arquivo sem coluna de fazenda): todos os talhões com o código.
 * Tudo em rascunho — o usuário confere e confirma com "filtrar".
 */
async function importarCsv(event) {
	const file = event.target.files && event.target.files[0]
	event.target.value = '' // permite reimportar o mesmo arquivo
	if (!file) return

	// Sem as opções carregadas (request pendente/falho) o matching marcaria TUDO
	// como não encontrado — melhor avisar do que enganar.
	if (props.fazendas.length === 0 && props.talhoes.length === 0) {
		csvResumo.value = {
			fazendas: 0,
			talhoes: 0,
			naoEncontrados: [],
			semColunas: false,
			semOpcoes: true,
		}
		return
	}

	const texto = await lerArquivoComoTexto(file)
	const { data, meta } = Papa.parse(texto, { header: true, skipEmptyLines: true })

	const normalizar = (s) =>
		String(s ?? '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim()
			.toLowerCase()

	// lower/trim SEM remover acentos: usado no match EXATO de grafia, que tem
	// prioridade sobre o acento-insensível para desambiguar cadastros homônimos
	// (ex.: "SAO JOAO" e "SÃO JOÃO" são fazendas distintas — a grafia do CSV
	// decide qual; sem match exato, cai no normalizado e vale qualquer homônima).
	const normalizarLeve = (s) =>
		String(s ?? '')
			.normalize('NFC')
			.trim()
			.toLowerCase()

	const colunas = (meta.fields || []).map((f) => ({ original: f, chave: normalizar(f) }))
	const colFazenda = colunas.find((c) => c.chave.includes('fazenda'))
	const colTalhao = colunas.find((c) => c.chave.includes('talhao'))

	if (!colFazenda && !colTalhao) {
		csvResumo.value = {
			fazendas: 0,
			talhoes: 0,
			naoEncontrados: [],
			semColunas: true,
			semOpcoes: false,
		}
		return
	}

	// Índices das opções do acervo processado:
	// fazenda por código OU nome normalizado -> CONJUNTO de ids. Homônimas
	// existem no cadastro (ex.: "SAO JOAO" e "SÃO JOÃO" com códigos distintos
	// normalizam para a mesma chave) — um Map de id único descartava uma delas
	// e os talhões da descartada viravam falsos "não encontrados".
	const fazendasPorChaveExata = new Map()
	const fazendasPorChave = new Map()
	const indexar = (mapa, chave, id) => {
		if (!chave) return
		if (!mapa.has(chave)) mapa.set(chave, new Set())
		mapa.get(chave).add(id)
	}
	props.fazendas.forEach((f) => {
		indexar(fazendasPorChaveExata, normalizarLeve(f.codigo), f.id)
		indexar(fazendasPorChaveExata, normalizarLeve(f.nome), f.id)
		indexar(fazendasPorChave, normalizar(f.codigo), f.id)
		indexar(fazendasPorChave, normalizar(f.nome), f.id)
	})
	// código do talhão por id (para montar o índice por fazenda)
	const codigoPorTalhaoId = new Map(props.talhoes.map((t) => [t.id, normalizar(t.codigo)]))
	// fazendaId -> (código do talhão -> [talhaoIds]) e fazendaId -> todos os talhões
	const talhoesPorFazenda = new Map()
	props.combinacoes.forEach((c) => {
		if (!c.talhaoId) return
		const codigo = codigoPorTalhaoId.get(c.talhaoId)
		if (codigo === undefined) return
		let porCodigo = talhoesPorFazenda.get(c.fazendaId)
		if (!porCodigo) {
			porCodigo = new Map()
			talhoesPorFazenda.set(c.fazendaId, porCodigo)
		}
		if (!porCodigo.has(codigo)) porCodigo.set(codigo, new Set())
		porCodigo.get(codigo).add(c.talhaoId)
	})
	// código do talhão -> [talhaoIds] (qualquer fazenda) — para arquivo só-talhão
	const talhoesPorCodigo = new Map()
	props.talhoes.forEach((t) => {
		const codigo = normalizar(t.codigo)
		if (!talhoesPorCodigo.has(codigo)) talhoesPorCodigo.set(codigo, new Set())
		talhoesPorCodigo.get(codigo).add(t.id)
	})

	const fazendasIds = new Set()
	const talhoesIds = new Set()
	const fazendasSoltas = new Set() // fazendas SEM talhão em alguma linha: entram inteiras
	const naoEncontrados = new Set()
	const paresVistos = new Set()

	data.forEach((linha) => {
		const brutoFazenda = colFazenda ? linha[colFazenda.original] : ''
		const valorFazenda = normalizar(brutoFazenda)
		const valorFazendaExato = normalizarLeve(brutoFazenda)
		const valorTalhao = colTalhao ? normalizar(linha[colTalhao.original]) : ''
		if (!valorFazenda && !valorTalhao) return

		const chavePar = `${valorFazenda}|${valorTalhao}`
		if (paresVistos.has(chavePar)) return
		paresVistos.add(chavePar)

		if (valorFazenda && valorTalhao) {
			const idsFazenda =
				fazendasPorChaveExata.get(valorFazendaExato) || fazendasPorChave.get(valorFazenda)
			if (!idsFazenda) {
				naoEncontrados.add(`${valorFazenda} > ${valorTalhao}`)
				return
			}
			// Procura o talhão em TODAS as fazendas homônimas — só é
			// não-encontrado se nenhuma delas o tiver.
			let encontrou = false
			idsFazenda.forEach((fazendaId) => {
				const idsDoTalhao = talhoesPorFazenda.get(fazendaId)?.get(valorTalhao)
				if (idsDoTalhao) {
					encontrou = true
					fazendasIds.add(fazendaId)
					idsDoTalhao.forEach((id) => talhoesIds.add(id))
				}
			})
			if (!encontrou) naoEncontrados.add(`${valorFazenda} > ${valorTalhao}`)
		} else if (valorFazenda) {
			const idsFazenda =
				fazendasPorChaveExata.get(valorFazendaExato) || fazendasPorChave.get(valorFazenda)
			if (!idsFazenda) {
				naoEncontrados.add(valorFazenda)
				return
			}
			idsFazenda.forEach((fazendaId) => {
				fazendasIds.add(fazendaId)
				fazendasSoltas.add(fazendaId)
			})
		} else {
			const idsDoTalhao = talhoesPorCodigo.get(valorTalhao)
			if (!idsDoTalhao) {
				naoEncontrados.add(valorTalhao)
				return
			}
			idsDoTalhao.forEach((id) => talhoesIds.add(id))
		}
	})

	// Fazenda sem talhão no arquivo entra INTEIRA: todos os talhões dela vão
	// para a lista (o AND fazenda×talhão do back exige — sem isso a fazenda
	// solta seria excluída pelos talhões dos pares).
	if (fazendasSoltas.size > 0 && talhoesIds.size > 0) {
		fazendasSoltas.forEach((fazendaId) => {
			const porCodigo = talhoesPorFazenda.get(fazendaId)
			if (porCodigo) porCodigo.forEach((ids) => ids.forEach((id) => talhoesIds.add(id)))
		})
	}

	rascunho.fazendasIds = [...fazendasIds]
	rascunho.talhoesIds = [...talhoesIds]
	csvResumo.value = {
		fazendas: fazendasIds.size,
		talhoes: talhoesIds.size,
		naoEncontrados: [...naoEncontrados],
		semColunas: false,
		semOpcoes: false,
	}
}

onClickOutside(painel, fechar)
</script>

<template>
	<div class="relative">
		<s-f-button
			icon="flowbite:filter-solid"
			secondary
			:active="temFiltroNoRascunho"
			:disabled="disabled"
			@click="abrir"
		/>
		<div
			v-show="aberto"
			ref="painel"
			class="bg-white-neutral border border-black-neutral rounded min-w-80 absolute z-10 p-3 flex flex-col gap-4 top-full left-0 mt-1"
		>
			<!-- Ordem hierárquica do domínio: Fazenda -> Estrato Florestal -> Talhão -->
			<dropdown
				v-model="rascunho.fazendasIds"
				:options="fazendasOptions"
				:label="$t('farm')"
				:placeholder="$t('selecioneOuPesquise')"
			/>
			<dropdown
				v-model="rascunho.estratosIds"
				:options="estratosOptions"
				:label="$t('forestStratification')"
				:placeholder="$t('selecioneOuPesquise')"
			/>
			<dropdown
				v-model="rascunho.talhoesIds"
				:options="talhoesOptions"
				:label="$t('talhao')"
				:placeholder="$t('selecioneOuPesquise')"
			/>

			<!-- Importação de CSV: preenche o rascunho de fazendas/talhões acima -->
			<div class="flex flex-col gap-1">
				<label class="text-label">{{ $t('importCsvFilter') }}</label>
				<s-f-button
					:text="$t('import')"
					secondary
					icon="uil:file-upload"
					class="justify-center"
					@click="inputCsv.click()"
				/>
				<input
					ref="inputCsv"
					type="file"
					accept=".csv,.txt"
					class="hidden"
					@change="importarCsv"
				/>
				<span
					v-if="csvResumo && csvResumo.semOpcoes"
					class="text-s-bold !text-red-600"
				>
					{{ $t('csvOptionsNotLoaded') }}
				</span>
				<span
					v-else-if="csvResumo && csvResumo.semColunas"
					class="text-s-bold !text-red-600"
				>
					{{ $t('csvMissingColumns') }}
				</span>
				<span
					v-else-if="csvResumo"
					class="text-s-bold"
				>
					{{ $t('csvFilterApplied') }}: {{ csvResumo.fazendas }} +
					{{ csvResumo.talhoes }}
					<span
						v-if="csvResumo.naoEncontrados.length > 0"
						class="!text-red-600"
						:title="csvResumo.naoEncontrados.join(', ')"
					>
						({{ $t('csvItemsNotFound') }}: {{ csvResumo.naoEncontrados.length }})
						<button
							type="button"
							class="underline cursor-pointer"
							:title="$t('copiar')"
							@click="copiarNaoEncontrados"
						>
							{{ copiado ? $t('copiado') : $t('copiar') }}
						</button>
					</span>
				</span>
			</div>

			<div class="flex justify-between gap-4">
				<s-f-button
					:text="$t('clearFilters')"
					secondary
					class="w-1/2 justify-center"
					@click="limpar"
				/>
				<s-f-button
					:text="$t('filter')"
					class="w-1/2 justify-center"
					@click="aplicar"
				/>
			</div>
		</div>
	</div>
</template>
